import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { consumeRefreshResult, type RefreshResult } from '../hooks/useForceRefresh';

/**
 * Показва изхода от принудителното обновяване СЛЕД презареждането.
 *
 * Без това операцията е няма: натискаш „Обнови", страницата се презарежда и
 * не научаваш нищо — нито дали е свършила работа, нито дали е паднала.
 * Резултатът се чете веднъж (`consumeRefreshResult` трие ключа), тоест
 * съобщението не се появява при следващите отваряния.
 */

const VISIBLE_MS = 5000;

export default function RefreshResultToast() {
  const { t } = useTranslation();
  const [result, setResult] = useState<RefreshResult | null>(null);

  useEffect(() => {
    const stored = consumeRefreshResult();
    if (!stored) return;
    setResult(stored);

    // Само успехът изчезва сам. Провалът остава, докато потребителят го
    // затвори — иначе съобщението, което носи лошата новина, е и единственото,
    // което може да се пропусне.
    if (!stored.ok) return;
    const timer = setTimeout(() => setResult(null), VISIBLE_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!result) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[9999]"
      style={{ bottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}
    >
      <div
        className={`rounded-lg shadow-lg p-4 flex items-start justify-between gap-3 text-white ${
          result.ok ? 'bg-green-600' : 'bg-red-600'
        }`}
      >
        <div className="flex items-start gap-3">
          {result.ok ? (
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.33 16a2 2 0 001.74 3z" />
            </svg>
          )}
          <div className="text-sm">
            <div className="font-medium">
              {result.ok ? t('common.refreshResult.done') : t('common.refreshResult.failed')}
            </div>
            <div className="opacity-90 mt-0.5">
              {result.ok
                ? t('common.refreshResult.cleared', {
                    caches: result.caches,
                    workers: result.serviceWorkers,
                  })
                : t('common.refreshResult.failedHint')}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setResult(null)}
          className="opacity-80 hover:opacity-100 p-1 flex-shrink-0"
          aria-label={t('common.refreshResult.closeLabel')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
